import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { SelectedFoodItemsInfo } from './useSelectAndExport'
import { Food } from '../../../../core/db_food'
import { Checkbox } from '../../../../component/Form/Elements/Checkbox'
import { SimpleButton } from '../../../../component/Form/Button/SimpleButton'
import cl from './index.module.scss'
import { Input } from '../../../../component/Input'

export type SelectAndExportPanelProps = {
    show: boolean
    selectedFoodsInfo: SelectedFoodItemsInfo
    selectedFoods: Record<number, Food>
    importFoods: Food[]
    setSelectedFoodsInfo: (info: SelectedFoodItemsInfo) => void
    unselectAll: () => void
    onSelectAllFoods: () => void
    onImportProducts?: () => void
    onExportProducts?: () => void
    onChangeCustomTag: (mode: 'none' | 'save' | 'replace', tag: string) => void
}

export const SelectAndExportPanel: React.FunctionComponent<SelectAndExportPanelProps> = (props) => {
    const [tagsMode, setTagsMode] = useState<'none' | 'save' | 'replace'>('save')
    const [customTag, setCustomTag] = useState('')
    useEffect(() => {
        if (props.show) {
            setTagsMode('save')
            setCustomTag('')
        }
    }, [props.show])
    const onChangeCustomTag = useCallback((tagsMode: 'none' | 'save' | 'replace', tag: string) => {
        setTagsMode(tagsMode)
        setCustomTag(tag)
        props.onChangeCustomTag(tagsMode, tag)
    }, [props.onChangeCustomTag])
    const settings = useMemo(() => {
        const mode: 'wait' | 'import' | 'export' = function() {
            if (props.importFoods.length > 0) {
                return 'import'
            }
            if (Object.keys(props.selectedFoods).length > 0) {
                return 'export'
            }
            return 'wait'
        }()
        const countFoods = function() {
            switch (mode) {
                case 'import':
                    return props.importFoods.length
                case 'export':
                    return Object.keys(props.selectedFoods).length
                case 'wait':
                    return 0
            }
        }()
        const title = function() {
            switch (mode) {
                case 'import':
                    return 'Импортирование продуктов'
                case 'export':
                    return 'Экспорт продуктов'
                case 'wait':
                    return 'Импорт и экспорт'
            }
        }()
        const hint = function() {
            const tagTitle = tagsMode === 'save'
                ? {
                    title: 'и добавить тег',
                    placeholder: 'пусто - не добавлять тег',
                } : tagsMode === 'replace'
                    ? {
                        title: 'на тег',
                        placeholder: 'пусто - без тега',
                    } : {
                        title: undefined,
                        placeholder: undefined,
                    }
            switch (mode) {
                case 'import':
                    return (
                        <>
                            <p className={cl.export_panel__hint}>
                                Ознакомьтесь и проверьте корректность списка импортируемых продуктов и их параметров,
                                так же выберите настройку тегов (или их отсутствие).
                                После чего нажмите кнопку &quot;Добавить всё&quot;.
                            </p>
                            <ImportActions {...props}
                                title={tagTitle.title}
                                placeholder={tagTitle.placeholder}
                                customTag={customTag}
                                tagsMode={tagsMode}
                                onTagsChange={onChangeCustomTag}
                            />
                        </>
                    )
                case 'export':
                    return (
                        <p className={cl.export_panel__hint}>
                            Выберите желаемые для экспорта продукты в списке.
                        </p>
                    )
                case 'wait':
                    return (
                        <p className={cl.export_panel__hint}>
                            Отметьте продукты в списке для экспорта или загрузите набор из файла (импорт).
                        </p>
                    )
            }
        }()
        const actions = function() {
            switch (mode) {
                case 'import':
                    return undefined
                case 'export':
                    return <ExportActions {...props} />
                case 'wait':
                    return <WaitActions {...props} />
            }
        }()
        return {
            mode,
            countFoods,
            title,
            hint,
            actions,
        }
    }, [props.importFoods, props.selectedFoods, tagsMode, setTagsMode])
    return (
        <>
            <PanelTitle show={props.show} title={settings.title} countFoods={settings.countFoods} />
            <PanelBody show={props.show}>
                {settings.hint}
            </PanelBody>
            {
                settings.mode !== 'import' && (
                    <PanelActions show={props.show}>
                        <div className={cl.export_panel_actions__actions}>
                            {settings.actions}
                        </div>
                    </PanelActions>
                )
            }
        </>
    )
}

const WaitActions: React.FunctionComponent<SelectAndExportPanelProps> = (props) => {
    return (
        <>
            <span className={cl.export_panel_actions__btn_wrap}>
                <SimpleButton
                    key="import"
                    size='small'
                    type="light-success"
                    click={() => props.onImportProducts?.()}
                    disabled={props.onImportProducts == null}
                >Импорт</SimpleButton>
            </span>
            <span className={cl.export_panel_actions__btn_wrap}>
                <SimpleButton
                    key="export"
                    size='small'
                    type="light-success"
                    click={() => props.onExportProducts?.()}
                    disabled={true}
                >Экспорт</SimpleButton>
            </span>
            <SimpleButton
                key="reset"
                size='small'
                type="transparent"
                click={props.unselectAll}
                disabled={true}
            >Сброс</SimpleButton>
        </>
    )
}


const ExportActions: React.FunctionComponent<SelectAndExportPanelProps> = (props) => {
    return (
        <>
            <span className={cl.export_panel_actions__btn_wrap}>
                <SimpleButton
                    key="select-all"
                    size='small'
                    type="light-success"
                    click={props.onSelectAllFoods}
                >Выбрать всё</SimpleButton>
            </span>
            <span className={cl.export_panel_actions__btn_wrap}>
                <SimpleButton
                    key="export"
                    size='small'
                    type="light-success"
                    click={() => props.onExportProducts?.()}
                >Экспорт</SimpleButton>
            </span>
            <SimpleButton
                key="reset"
                size='small'
                type="transparent"
                click={props.unselectAll}
                disabled={false}
            >Сброс</SimpleButton>
        </>
    )
}

const ImportActions: React.FunctionComponent<SelectAndExportPanelProps & {
    title?: string
    placeholder?: string
    customTag: string
    tagsMode: 'none' | 'save' | 'replace'
    onTagsChange: (tagsMode: 'none' | 'save' | 'replace', tag: string) => void
}> = (props) => {
    const [tag, setTag] = useState(props.customTag)
    const onTagsChange = useCallback((value: string) => {
        setTag(value)
    }, [props.onTagsChange, props.customTag, props.tagsMode])
    return (
        <>
            <div className={cl.tags}>
                <SimpleButton size='small' type={props.tagsMode === 'none' ? 'light-success' : 'transparent'}
                    click={() => props.onTagsChange('none', props.customTag)}>Без тегов</SimpleButton>
                <SimpleButton size='small' type={props.tagsMode === 'save' ? 'light-success' : 'transparent'}
                    click={() => props.onTagsChange('save', props.customTag)}>Сохранить</SimpleButton>
                <SimpleButton size='small' type={props.tagsMode === 'replace' ? 'light-success' : 'transparent'}
                    click={() => props.onTagsChange('replace', props.customTag)}>Заменить</SimpleButton>
            </div>
            {
                props.title && (
                    <div className={cl.input__find}>
                        <span>{props.title}:</span>
                        <Input value={tag}
                            name='tag'
                            autofocus={true}
                            placeholder={props.placeholder}
                            onChange={onTagsChange}
                            onBlur={() => props.onTagsChange(props.tagsMode, tag.trim())}
                            onKeyEnter={() => props.onTagsChange(props.tagsMode, tag.trim())}/>
                    </div>
                )
            }
        </>
    )
}


const PanelTitle: React.FunctionComponent<{
    title: string
    countFoods: number
    show: boolean
}> = ({ title, countFoods, show }) => {
    return (
        <div className={cl.export_panel_title + ' ' + (show ? cl.export_panel_title_show : cl.export_panel_title_hide)}>
            <div className={cl.export_panel_title__inner}>
                <div className={cl.export_panel_title__head}>
                    <span className={cl.export_panel_title__title}>{title}</span>
                    {countFoods > 0 ? <span className={cl.export_panel_title__badge}>{countFoods}</span> : null}
                </div>
            </div>
        </div>

    )
}

const PanelBody: React.FunctionComponent<{
    show: boolean
    children: React.ReactNode
}> = ({ show, children }) => {
    return (
        <div className={cl.export_panel + ' ' + (show ? cl.export_panel_show : cl.export_panel_hide)}>
            <div className={cl.export_panel__inner}>
                <div className={cl.export_panel__body}>
                    {children}
                </div>
            </div>
        </div>
    )
}

const PanelActions: React.FunctionComponent<{
    show: boolean
    children: React.ReactNode
}> = ({ show, children }) => {
    return (
        <div className={cl.export_panel_actions + ' ' + (show ? cl.export_panel_actions_show : cl.export_panel_actions_hide)}>
            <div className={cl.export_panel_actions__inner}>
                <div className={cl.export_panel_actions__actions}>
                    {children}
                </div>
            </div>
        </div>
    )
}